/* eslint-disable no-console */
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import {
  saveOportunidad,
  saveContrato,
  updateOportunidad,
  updateCliente,
  cloneOportunidad,
  registrarCredito,
} from "../../../utils";
import { upsertCotizacionPorCodigo } from "../../../utils/cotizacionesGuardadas";
import { pdf } from "@react-pdf/renderer";

/** Genera rango de meses entre inicio y fin (inclusive). */
function generateMonthRange(startMonth, startYear, endMonth, endYear) {
  const months = [];
  let m = startMonth;
  let y = startYear;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    months.push({ month: m, year: y });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return months;
}

/** Formatea mes/año a "MM-YYYY". */
function formatMes({ month, year }) {
  return `${String(month).padStart(2, "0")}-${year}`;
}

/**
 * Hook que encapsula los handlers de guardado, actualización y clonación
 * de oportunidades, junto con sus estados de loading.
 */
export function useOportunidadHandlers({
  clientes,
  userId,
  isProgrammaticSelected,
  inventarioData,
  inversionFinalTotal,
  edificiosUnicosTotal,
  ascensoresSeleccionados,
  totemsSeleccionados,
  coworkSeleccionados,
  esperaSeleccionados,
  frecuencia,
  plazo,
  duracion,
  pais,
  horasSeleccionadas,
  codigoGenerado,
  opportunityData,
  manualDiscountAmount,
  finalAmount,
  forceDollars,
  editMesCierre,
  editAnoCierre,
  editMesFactura,
  editAnoFactura,
  editEstatus,
  validateData,
  calculateProposalChanges,
  navigate,
  setShowLinkModal,
  setClienteParaDescarga,
  currentOpportunityId,
  audienciaSeleccionadaTotal,
  salidasTotales,
  impactosTotales,
  tarifaFriaTotal,
  cpmTotalFinal,
  descuentoGlobal,
}) {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cloningLoading, setCloningLoading] = useState(false);

  // --- Construir datos de oportunidad ---
  const buildOportunidadData = useCallback(
    (data, selectedClient, updatedAt = null) => {
      const clienteMatch = selectedClient ? clientes.find((c) => c.nombre === selectedClient.nombre) : null;
      const clientId = clienteMatch?.id || null;
      const edificiosparticipantesIdsArray = [
        ...(inventarioData.edificiosSeleccionadosAscensores || []),
        ...(inventarioData.edificiosSeleccionadosTotems || []),
        ...(inventarioData.edificiosSeleccionadosCowork || []),
        ...(inventarioData.edificiosSeleccionadosEspera || []),
      ].filter(Boolean);
      const uniqueEdificiosparticipantesIds = [...new Set(edificiosparticipantesIdsArray)].join(",");
      const ascensoresIds = (inventarioData.edificiosSeleccionadosAscensores || []).join(",");
      const circulacionIds = (inventarioData.edificiosSeleccionadosTotems || []).join(",");
      const esperaIds = (inventarioData.edificiosSeleccionadosEspera || []).join(",");
      const coworkIds = (inventarioData.edificiosSeleccionadosCowork || []).join(",");
      const currentVersion = opportunityData?.version ? parseInt(opportunityData.version, 10) : 0;
      const newVersion = updatedAt ? currentVersion + 1 : 1;
      const hasManualDiscount = manualDiscountAmount > 0;
      const finalImporte = hasManualDiscount ? Math.floor(finalAmount) : Math.floor(inversionFinalTotal);

      const formatMonthYear = (month, year) => {
        if (!month || !year || isNaN(parseInt(month, 10)) || isNaN(parseInt(year, 10))) {
          return null;
        }
        return `${month.padStart(2, "0")}-${year}`;
      };

      const oportunidadData = {
        cliente: data.clientePublicitario || opportunityData?.cliente || "Sin cliente",
        client_id: clientId,
        campana: data.campana || opportunityData?.campana || null,
        canal: data.canal || opportunityData?.canal || null,
        mes_factura: formatMonthYear(data.mes_factura, data.ano_factura) || opportunityData?.mes || null,
        mes_cierre: formatMonthYear(data.mes_cierre, data.ano_cierre) || opportunityData?.mes_cierre || null,
        ano: parseInt(data.ano_factura || new Date().getFullYear().toString(), 10),
        importe: finalImporte,
        estatus: data.estatus || opportunityData?.estatus || "0%",
        ejecutivo: clienteMatch?.ejecutivo ?? (userId ? parseInt(userId, 10) : null),
        tipo: isProgrammaticSelected ? "p" : "d",
        edificiosparticipantes: uniqueEdificiosparticipantesIds || "",
        edificiostotales: edificiosUnicosTotal || 0,
        pantallastotales:
          (ascensoresSeleccionados || 0) +
          (totemsSeleccionados || 0) +
          (coworkSeleccionados || 0) +
          (esperaSeleccionados || 0),
        pantallas_ascensores: ascensoresIds || "",
        pantallas_circulacion: circulacionIds || "",
        pantallas_espera: esperaIds || "",
        pantallas_cowork: coworkIds || "",
        formatopauta: frecuencia?.toString() || "0",
        periodopauta: plazo || 0,
        duracion: duracion || 0,
        pais: pais || "Chile",
        facturafecha: opportunityData?.facturafecha || null,
        plazocobro: opportunityData?.plazocobro || null,
        facturafechacobro: opportunityData?.facturafechacobro || null,
        facturanumero: opportunityData?.facturanumero || null,
        eliminada: false,
        code: updatedAt && opportunityData?.code ? opportunityData.code : codigoGenerado,
        comision_agencia: opportunityData?.comision_agencia || null,
        horas_pauta: horasSeleccionadas.horas || 0,
        intervalos: horasSeleccionadas.intervalos ? JSON.stringify(horasSeleccionadas.intervalos) : null,
        options: horasSeleccionadas.options ? JSON.stringify(horasSeleccionadas.options) : null,
        moneda_dolar: forceDollars,
        version: newVersion,
        has_manual_discount: hasManualDiscount,
        manual_discount_amount: hasManualDiscount ? Math.floor(manualDiscountAmount) : null,
        original_amount: hasManualDiscount ? Math.floor(inversionFinalTotal) : null,
        // Métricas del cotizador
        audiencia_total: audienciaSeleccionadaTotal || null,
        impactos_totales: impactosTotales || null,
        salidas_totales: salidasTotales || null,
        tarifa_fria_total: tarifaFriaTotal || null,
        descuento_pct: descuentoGlobal || null,
        cpm_total: cpmTotalFinal || null,
        cpm_exhibicion: salidasTotales > 0 ? (inversionFinalTotal / salidasTotales) * 1000 : null,
      };
      if (updatedAt) {
        oportunidadData.updated_at = updatedAt;
      }
      return oportunidadData;
    },
    [
      clientes,
      inventarioData,
      manualDiscountAmount,
      finalAmount,
      inversionFinalTotal,
      opportunityData,
      userId,
      isProgrammaticSelected,
      edificiosUnicosTotal,
      ascensoresSeleccionados,
      totemsSeleccionados,
      coworkSeleccionados,
      esperaSeleccionados,
      frecuencia,
      plazo,
      duracion,
      pais,
      horasSeleccionadas,
      codigoGenerado,
      forceDollars,
      audienciaSeleccionadaTotal,
      impactosTotales,
      salidasTotales,
      tarifaFriaTotal,
      cpmTotalFinal,
      descuentoGlobal,
    ]
  );

  // --- Construir datos de contrato multi-mes ---
  const buildContratoData = useCallback(
    (data, selectedClient) => {
      const clienteMatchC = selectedClient ? clientes.find((c) => c.nombre === selectedClient.nombre) : null;
      const clientId = clienteMatchC?.id || null;

      const edificiosIdsArray = [
        ...(inventarioData.edificiosSeleccionadosAscensores || []),
        ...(inventarioData.edificiosSeleccionadosTotems || []),
        ...(inventarioData.edificiosSeleccionadosCowork || []),
        ...(inventarioData.edificiosSeleccionadosEspera || []),
      ].filter(Boolean);
      const uniqueEdificiosIds = [...new Set(edificiosIdsArray)].join(",");
      const pantallastotales =
        (ascensoresSeleccionados || 0) +
        (totemsSeleccionados || 0) +
        (coworkSeleccionados || 0) +
        (esperaSeleccionados || 0);

      // IDs por tipo de pantalla (para comisión edificios)
      const contratoAscensoresIds = (inventarioData.edificiosSeleccionadosAscensores || []).join(",");
      const contratoCirculacionIds = (inventarioData.edificiosSeleccionadosTotems || []).join(",");
      const contratoEsperaIds = (inventarioData.edificiosSeleccionadosEspera || []).join(",");
      const contratoCoworkIds = (inventarioData.edificiosSeleccionadosCowork || []).join(",");

      const hasManualDiscount = manualDiscountAmount > 0;
      const totalImporte = hasManualDiscount ? Math.floor(finalAmount) : Math.floor(inversionFinalTotal);

      const formatMonthYear = (month, year) => {
        if (!month || !year) return null;
        return `${String(month).padStart(2, "0")}-${year}`;
      };

      // Determinar rango de meses
      const isMultiMonth = data.is_multi_month;
      let sm, sy, em, ey;

      if (isMultiMonth) {
        sm = parseInt(data.multi_month_start_month);
        sy = parseInt(data.multi_month_start_year);
        em = parseInt(data.multi_month_end_month);
        ey = parseInt(data.multi_month_end_year);
      } else {
        // Audiencia referencia sin multi-mes → contrato de 1 mes
        sm = parseInt(data.mes_factura);
        sy = parseInt(data.ano_factura);
        em = sm;
        ey = sy;
      }

      const mesDefaults = {
        edificios_ids: uniqueEdificiosIds || null,
        edificios_totales: uniqueEdificiosIds ? uniqueEdificiosIds.split(",").filter(Boolean).length : 0,
        pantallas_totales: pantallastotales || 0,
        personas_asignadas: null,
        pantallas_ascensores: contratoAscensoresIds || "",
        pantallas_espera: contratoEsperaIds || "",
        pantallas_circulacion: contratoCirculacionIds || "",
        pantallas_cowork: contratoCoworkIds || "",
        facturafecha: null,
        facturanumero: null,
        plazocobro: null,
        facturafechacobro: null,
      };

      const distribucion = data.distribucion_tipo || "prorrateo";
      const range = generateMonthRange(sm, sy, em, ey);

      // plazo_modo: "mes_tipo" = cotizado es por mes, "total" = cotizado es el total
      const plazoModo = data.plazo_modo || "total";
      const isMesTipo = plazoModo === "mes_tipo" && isMultiMonth;
      const effectiveTotal = isMesTipo ? totalImporte * range.length : totalImporte;

      const baseAmount = Math.floor(effectiveTotal / range.length);
      const remainder = effectiveTotal - baseAmount * range.length;
      const mesesJSON = range.map((m, i) => ({
        mes: formatMes(m),
        importe: i === 0 ? baseAmount + remainder : baseAmount,
        ...mesDefaults,
      }));

      return {
        client_id: clientId,
        cliente: data.clientePublicitario || "Sin cliente",
        ejecutivo: clienteMatchC?.ejecutivo ?? (userId ? parseInt(userId, 10) : null),
        pais: pais || "Chile",
        canal: data.canal || null,
        campana: data.campana || null,
        code: codigoGenerado,
        importe_total: effectiveTotal,
        meses_actividad: mesesJSON.length,
        importe_mensual: Math.floor(effectiveTotal / mesesJSON.length),
        mes_inicio: mesesJSON[0].mes,
        mes_fin: mesesJSON.at(-1).mes,
        mes_cierre: formatMonthYear(data.mes_cierre, data.ano_cierre),
        pauta_mes_inicio: formatMonthYear(data.pauta_start_month, data.pauta_start_year),
        pauta_mes_fin: formatMonthYear(data.pauta_end_month, data.pauta_end_year),
        ano: sy,
        distribucion_tipo: distribucion,
        personas_comprometidas: data.audiencia_referencia
          ? parseInt(String(data.audiencia_contratada).replace(/[\s,.]/g, "")) || null
          : null,
        edificios_referencia: uniqueEdificiosIds || "",
        edificios_referencia_totales: edificiosUnicosTotal || 0,
        pantallas_referencia_totales: pantallastotales,
        tipo: isProgrammaticSelected ? "p" : "d",
        formatopauta: frecuencia?.toString() || "0",
        duracion: duracion || 0,
        horas_pauta: horasSeleccionadas.horas || 0,
        intervalos: horasSeleccionadas.intervalos ? JSON.stringify(horasSeleccionadas.intervalos) : null,
        options: horasSeleccionadas.options ? JSON.stringify(horasSeleccionadas.options) : null,
        periodopauta: plazo || 0,
        plazo_modo: isMultiMonth ? plazoModo : null,
        meses: mesesJSON,
        estatus: data.estatus || "50%",
        moneda_dolar: forceDollars,
        version: 1,
        has_manual_discount: hasManualDiscount,
        manual_discount_amount: hasManualDiscount
          ? Math.floor(isMesTipo ? manualDiscountAmount * range.length : manualDiscountAmount)
          : null,
        original_amount: hasManualDiscount
          ? Math.floor(isMesTipo ? inversionFinalTotal * range.length : inversionFinalTotal)
          : null,
        comision_agencia: null,
        // Métricas del cotizador
        audiencia_total: audienciaSeleccionadaTotal || null,
        impactos_totales: impactosTotales || null,
        salidas_totales: salidasTotales || null,
        tarifa_fria_total: tarifaFriaTotal || null,
        descuento_pct: descuentoGlobal || null,
        cpm_total: cpmTotalFinal || null,
        cpm_exhibicion: salidasTotales > 0 ? (inversionFinalTotal / salidasTotales) * 1000 : null,
      };
    },
    [
      clientes,
      inventarioData,
      ascensoresSeleccionados,
      totemsSeleccionados,
      coworkSeleccionados,
      esperaSeleccionados,
      manualDiscountAmount,
      finalAmount,
      inversionFinalTotal,
      userId,
      pais,
      codigoGenerado,
      edificiosUnicosTotal,
      isProgrammaticSelected,
      frecuencia,
      duracion,
      horasSeleccionadas,
      plazo,
      forceDollars,
      audienciaSeleccionadaTotal,
      impactosTotales,
      salidasTotales,
      tarifaFriaTotal,
      cpmTotalFinal,
      descuentoGlobal,
    ]
  );

  // --- Vincular nueva oportunidad o contrato ---
  const handleLinkOportunidad = useCallback(
    async (data, { generatePdfDocument, generateFileName }) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        if (!data.clientePublicitario) {
          toast.error("Por favor, selecciona un cliente publicitario.");
          setIsSubmitting(false);
          return;
        }

        const selectedClient = clientes.find((cliente) => cliente.nombre === data.clientePublicitario);
        if (!selectedClient) {
          toast.error("Cliente no encontrado. Por favor, selecciona un cliente válido.");
          setIsSubmitting(false);
          return;
        }

        const isContrato = data.is_multi_month || data.audiencia_referencia;
        const monedaCredito = forceDollars ? "USD" : pais === "Peru" ? "PEN" : "CLP";

        // --- Lógica de crédito ---
        const usaCredito = data.usar_credito && parseFloat(data.monto_credito) > 0;
        const montoCredito = usaCredito ? parseFloat(data.monto_credito) : 0;

        if (usaCredito) {
          // Calcular importe total de la cotización
          const hasDiscount = manualDiscountAmount > 0;
          const importeTotal = hasDiscount ? Math.floor(finalAmount) : Math.floor(inversionFinalTotal);
          const excedente = importeTotal - montoCredito;

          if (excedente > 0) {
            // Crédito no alcanza: guardar venta solo por el excedente
            if (isContrato) {
              const contratoData = buildContratoData(data, selectedClient);
              contratoData.importe_total = excedente;
              if (contratoData.meses_actividad > 0) {
                contratoData.importe_mensual = Math.floor(excedente / contratoData.meses_actividad);
              }
              await saveContrato(contratoData);
              try {
                await upsertCotizacionPorCodigo(codigoGenerado, {
                  estado: "ejecutada",
                  cliente_id: selectedClient?.id || null,
                  cliente_nombre: selectedClient?.empresa || selectedClient?.nombre || null,
                });
              } catch (cotError) {
                console.error("Error al actualizar cotización vinculada:", cotError);
              }
            } else {
              const oportunidadData = buildOportunidadData(data, selectedClient);
              oportunidadData.importe = excedente;
              await saveOportunidad(oportunidadData);
              try {
                await upsertCotizacionPorCodigo(codigoGenerado, {
                  estado: "ejecutada",
                  cliente_id: selectedClient?.id || null,
                  cliente_nombre: selectedClient?.empresa || selectedClient?.nombre || null,
                });
              } catch (cotError) {
                console.error("Error al actualizar cotización vinculada:", cotError);
              }
            }
            toast.success(`Venta registrada por excedente: ${monedaCredito} ${excedente.toLocaleString()}`);
          }
          // Si excedente <= 0: crédito cubre todo, NO se guarda venta

          // Registrar consumo de crédito (el monto consumido es el menor entre crédito y total)
          const montoConsumido = Math.min(montoCredito, importeTotal);
          try {
            await registrarCredito({
              clientId: selectedClient.id,
              pais,
              tipo: "consumo",
              monto: montoConsumido,
              moneda: monedaCredito,
              referenciaId: null,
              referenciaTipo: isContrato ? "sales_contratos" : "sales",
              descripcion: `Consumo crédito — ${data.campana || "sin campaña"}`,
              usuarioId: parseInt(userId, 10),
            });
            toast.success(`Crédito consumido: ${monedaCredito} ${montoConsumido.toLocaleString()}`);
          } catch (creditError) {
            console.error("Error al registrar consumo de crédito:", creditError);
            toast.error("Error al descontar el crédito.");
          }
        } else {
          // Sin crédito: flujo normal
          if (isContrato) {
            const contratoData = buildContratoData(data, selectedClient);
            await saveContrato(contratoData);
            try {
              await upsertCotizacionPorCodigo(codigoGenerado, {
                estado: "ejecutada",
                cliente_id: selectedClient?.id || null,
                cliente_nombre: selectedClient?.empresa || selectedClient?.nombre || null,
              });
            } catch (cotError) {
              console.error("Error al actualizar cotización vinculada:", cotError);
            }
          } else {
            const oportunidadData = buildOportunidadData(data, selectedClient);
            await saveOportunidad(oportunidadData);
            try {
              await upsertCotizacionPorCodigo(codigoGenerado, {
                estado: "ejecutada",
                cliente_id: selectedClient?.id || null,
                cliente_nombre: selectedClient?.empresa || selectedClient?.nombre || null,
              });
            } catch (cotError) {
              console.error("Error al actualizar cotización vinculada:", cotError);
            }
          }
        }

        // Actualizar clientes con info de contacto
        const today = new Date();
        const nextContactDate = new Date(today);
        nextContactDate.setDate(today.getDate() + 7);
        const formatDate = (date) => date.toISOString().split("T")[0];

        const updatedData = { id: selectedClient.id };
        if (data.tipoCotizacion === "cliente") {
          updatedData.ultimo_contacto_fecha_cliente = formatDate(today);
          updatedData.ultimo_contacto_accion_cliente = "QUOTE";
          updatedData.proximo_contacto_fecha_cliente = formatDate(nextContactDate);
          updatedData.proximo_contacto_accion_cliente = "FOLLOW UP";
        } else if (data.tipoCotizacion === "agencia") {
          updatedData.ultimo_contacto_fecha_agencia = formatDate(today);
          updatedData.ultimo_contacto_accion_agencia = "QUOTE";
          updatedData.proximo_contacto_fecha_agencia = formatDate(nextContactDate);
          updatedData.proximo_contacto_accion_agencia = "FOLLOW UP";
        }

        try {
          await updateCliente(updatedData);
        } catch (error) {
          console.error("Error updating cliente contact info:", error);
          toast.error(
            "Error al actualizar información de contacto del cliente, pero la oportunidad se vinculó correctamente."
          );
        }

        setClienteParaDescarga(data.clientePublicitario);
        setShowLinkModal(false);
        toast.success(
          usaCredito
            ? "Vinculación con crédito completada"
            : isContrato
              ? "Contrato vinculado correctamente"
              : "Oportunidad vinculada correctamente"
        );

        try {
          const pdfDoc = generatePdfDocument();
          const blob = await pdf(pdfDoc).toBlob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = generateFileName(data.clientePublicitario, data.campana, true);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (pdfError) {
          console.error("Error al generar el PDF:", pdfError);
          toast.error("Error al generar el PDF, pero la oportunidad se vinculó correctamente.");
        }
      } catch (error) {
        console.error("Error en handleLinkOportunidad:", error);
        toast.error(`Error al vincular: ${error.message}`);
      } finally {
        setTimeout(() => {
          setIsSubmitting(false);
        }, 1000);
      }
    },
    [
      isSubmitting,
      clientes,
      buildOportunidadData,
      buildContratoData,
      setShowLinkModal,
      setClienteParaDescarga,
      userId,
      pais,
      forceDollars,
      manualDiscountAmount,
      finalAmount,
      inversionFinalTotal,
      codigoGenerado,
    ]
  );

  // --- Actualizar oportunidad existente ---
  const handleUpdateOportunidad = useCallback(
    async ({ generatePdfDocument, generateFileName }) => {
      const formData = {
        mes_cierre: editMesCierre,
        ano_cierre: editAnoCierre,
        mes_factura: editMesFactura,
        ano_factura: editAnoFactura,
        estatus: editEstatus,
      };

      if (!validateData()) {
        toast.error("Por favor, valida los datos antes de actualizar.");
        return;
      }
      if (!currentOpportunityId || isNaN(currentOpportunityId)) {
        toast.error("ID de oportunidad no válido o no proporcionado");
        console.error("Invalid currentOpportunityId:", currentOpportunityId);
        setLoading(false);
        return;
      }
      if (!editMesCierre || !editAnoCierre || !editMesFactura || !editAnoFactura) {
        toast.error("Por favor, completa todos los campos de fecha");
        return;
      }
      const cierreFull = new Date(`${formData.ano_cierre}-${formData.mes_cierre.padStart(2, "0")}-01`);
      const facturaFull = new Date(`${formData.ano_factura}-${formData.mes_factura.padStart(2, "0")}-01`);
      if (isNaN(cierreFull.getTime()) || isNaN(facturaFull.getTime())) {
        toast.error("Fechas inválidas");
        setLoading(false);
        return;
      }
      if (cierreFull > facturaFull) {
        toast.error("Fecha de cierre no puede ser posterior a fecha de factura");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const selectedClient = clientes.find((cliente) => cliente.nombre === opportunityData?.cliente);
        const oportunidadData = buildOportunidadData(formData, selectedClient, new Date().toISOString());
        console.log("Opportunity data being sent:", oportunidadData);
        await updateOportunidad({ id: currentOpportunityId, oportunidad: oportunidadData });
        const proposalChanged = calculateProposalChanges();
        if (proposalChanged) {
          try {
            const pdfDoc = generatePdfDocument();
            const blob = await pdf(pdfDoc).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = generateFileName();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          } catch (pdfError) {
            console.error("Error al generar el PDF:", pdfError);
            toast.error("Error al generar el PDF, pero la oportunidad se actualizó correctamente.");
          }
        }
        toast.success("Oportunidad actualizada correctamente");
        navigate(-1);
      } catch (error) {
        console.error("Error al actualizar oportunidad:", error);
        toast.error(error.message || "Error al actualizar la oportunidad");
      } finally {
        setLoading(false);
      }
    },
    [
      validateData,
      clientes,
      opportunityData,
      buildOportunidadData,
      currentOpportunityId,
      calculateProposalChanges,
      navigate,
      editMesCierre,
      editAnoCierre,
      editMesFactura,
      editAnoFactura,
      editEstatus,
    ]
  );

  // --- Clonar oportunidad ---
  const handleCloneOportunidad = useCallback(async () => {
    if (!opportunityData) {
      toast.error("No hay datos de oportunidad para clonar");
      return;
    }

    try {
      setCloningLoading(true);
      const clonedOportunidad = await cloneOportunidad(opportunityData);

      if (clonedOportunidad) {
        toast.success(`Oportunidad clonada: ${clonedOportunidad.code}`);
        navigate(`/admin/online-cotizador/editar/${clonedOportunidad.id}`, {
          state: { oportunidad: clonedOportunidad },
        });
      }
    } catch (error) {
      console.error("Error al clonar oportunidad:", error);
      toast.error("Error al clonar la oportunidad");
    } finally {
      setCloningLoading(false);
    }
  }, [opportunityData, navigate]);

  return {
    loading,
    setLoading,
    isSubmitting,
    cloningLoading,
    handleLinkOportunidad,
    handleUpdateOportunidad,
    handleCloneOportunidad,
  };
}
